import React, {
  useState,
  useContext,
  useMemo,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import { TextField, Tooltip } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import * as Sentry from '@sentry/browser';

import ExternalCoachContext from '../../../../../../context/ExternalCoachContext';
import FirebaseContext from '../../../../../../../context/FirebaseContext';
import WorkoutContext from '../../../../../../context/WorkoutContext';
import LoadingOverlay from '../../../../../LoadingOverlay';
import User from '../../../../../../../Model/User';
import DialogRoundedModal from '../../../../../../../components/DialogRoundedModal';
import { SaveButton, CancelButton } from '../../../../../../../components/Button/ActionButtons';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import useBeforeUnloadGuard from '../../../../../../../hooks/useBeforeUnloadGuard';
import useLogger from '../../../../../../../hooks/useLogger';
import useToast from '../../../../../../hooks/useToast';
import useScheduleTimeLabel from '../../../../../../hooks/useScheduleTimeLabel';
import { CoachingActivity } from '../../../../../../../utils/log';
import { WorkoutAssignmentCode, validateWorkoutForAssignment } from '../../../../../../../utils/workout';
import { DateFormat, getDatesBetween } from '../../../../../../../utils/date';
import { ScheduledAssignmentType } from '../../../../../../../Model/ScheduledAssignment';
import AssignmentRepeatInput from '../AssignmentRepeatInput';

import QAPContext from '../../../../../../../context/QAPContext';
import AssignmentProductSelector from '../../../../../AssignmentProductSelector';
import {
  ColumnContainer,
  EquipmentChip,
  RowContainer,
  StyledAutocomplete,
  StyledOption,
  TimeChip,
  StyledOptionSubContent,
  StyledCheckBoxLabel,
  CheckBoxContainer,
  StyledCheckBox,
  RepeatDay,
  DaysContainer,
  SettingSection,
  StyledInputLabel,
  RepeatContainer,
  StyledAutocompleteWrapper,
} from './styles';
import texts from './texts.json';

const WorkoutAssignmentModal = ({
  showModal,
  onClose,
  userDoc,
  selectedDate,
}) => {
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [isKeepWindowOpen, setIsKeepWindowOpen] = useState(false);
  const [shouldRepeatWorkout, setShouldRepeatWorkout] = useState(false);
  const [assignmentRepetitionWeeks, setAssignmentRepetitionWeeks] = useState(1);
  const [isScheduled, setIsScheduled] = useState(true);
  // Seeded from the quick access panel, but only when it was opened for this client.
  const { productIdForUser } = useContext(QAPContext);
  const [selectedProductId, setSelectedProductId] = useState(productIdForUser(userDoc?.id));

  const { externalCoachDoc: { id: coachId } } = useContext(ExternalCoachContext);
  const { firebase } = useContext(FirebaseContext);
  const { workouts, isReady } = useContext(WorkoutContext);

  const isComponentMountedRef = useComponentMounted();
  useBeforeUnloadGuard(showLoading);
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const { scheduleTimeLabel } = useScheduleTimeLabel();

  const workoutOptions = useMemo(() => workouts.map((workout) => {
    const workoutStatus = validateWorkoutForAssignment(workout);
    return ({
      id: workout.id,
      label: workout.name,
      equipment: workout.equipmentList ? workout.equipmentList.join(', ') : '',
      workout,
      isValidWorkout: workoutStatus === WorkoutAssignmentCode.VALID_WORKOUT,
      workoutStatus,
    });
  }), [workouts]);

  // Subtract 1 day to avoid including the final day in the end date
  const endDate = useMemo(() => moment(selectedDate).add(assignmentRepetitionWeeks, 'weeks').subtract(1, 'days')
    .format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT), [
    selectedDate,
    assignmentRepetitionWeeks,
  ]);

  const onAssignWorkout = async () => {
    if (shouldRepeatWorkout && (selectedDays.length === 0)) {
      showToast(texts.validations.selectRepeatDays, { warning: true });
      return;
    }
    const allDates = getDatesBetween(selectedDate, endDate, selectedDays);
    const selectedAllDates = shouldRepeatWorkout && (allDates.length > 0) ? allDates : [selectedDate];
    setShowLoading(true);

    let response;
    try {
      response = await firebase.remote('workout-scheduleAssignmentEndpoint', {
        type: ScheduledAssignmentType.WORKOUT,
        userId: userDoc.id,
        coachId,
        displayName: selectedWorkout.label,
        isScheduled,
        payload: {
          workoutId: selectedWorkout.workout.id,
          dates: selectedAllDates,
        },
        ...(selectedProductId && { productId: selectedProductId }),
      });
    } catch (error) {
      // A network failure rejects instead of returning a response; without this the overlay never clears.
      Sentry.captureException(error);
    } finally {
      if (isComponentMountedRef.current) {
        setShowLoading(false);
      }
    }

    if (!response?.ok) {
      showToast(texts.scheduleError, { error: true });
      return;
    }

    showToast(isScheduled ? texts.workoutScheduled : texts.workoutAssigned);

    logCoachingActivity(CoachingActivity.ASSIGNED_WORKOUT, {
      workoutId: selectedWorkout.workout.id,
      clientId: userDoc.id,
      ...(selectedProductId && { productId: selectedProductId }),
    });
    if (isComponentMountedRef.current) {
      setSelectedWorkout(null);
      setSelectedDays([]);
      setAssignmentRepetitionWeeks(1);
      setShouldRepeatWorkout(false);
      if (!isKeepWindowOpen) {
        onClose();
      }
    }
  };

  const handleDaysSelect = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter((selectedDay) => selectedDay !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleCheckedRepeatWorkout = () => {
    setShouldRepeatWorkout((prev) => !prev);
    setSelectedDays([]);
    setAssignmentRepetitionWeeks(1);
  };

  const isMissingProduct = !!userDoc?.hasMultipleActiveProducts && !selectedProductId;

  return (
    <DialogRoundedModal
      title={texts.title}
      description={selectedDate}
      fullWidth
      maxWidth="sm"
      actionButtons={(
        <ColumnContainer>
          <CheckBoxContainer>
            <StyledCheckBox
              onChange={() => setIsScheduled((prev) => !prev)}
              checked={isScheduled}
              size="small"
            />
            <StyledCheckBoxLabel>{scheduleTimeLabel}</StyledCheckBoxLabel>
          </CheckBoxContainer>
          <CheckBoxContainer>
            <StyledCheckBox
              onChange={() => setIsKeepWindowOpen((prev) => !prev)}
              checked={isKeepWindowOpen}
              size="small"
            />
            <StyledCheckBoxLabel>{texts.keepWindowOpen}</StyledCheckBoxLabel>
          </CheckBoxContainer>
          <RowContainer>
            <SaveButton
              disabled={!selectedWorkout || isMissingProduct}
              icon={<CheckCircleOutline />}
              onClick={onAssignWorkout}
            >
              {texts.assignWorkout}
            </SaveButton>
            <CancelButton onClick={onClose}>Cancel</CancelButton>
          </RowContainer>
        </ColumnContainer>
      )}
      open={showModal}
      onClose={onClose}
    >
      <AssignmentProductSelector
        userDoc={userDoc}
        selectedProductId={selectedProductId}
        onSelect={setSelectedProductId}
      />
      <StyledAutocompleteWrapper>
        <StyledAutocomplete
          disablePortal
          value={selectedWorkout}
          options={workoutOptions}
          getOptionDisabled={(option) => !option.isValidWorkout}
          renderInput={(params) => <TextField {...params} label="Select Workout" />}
          onChange={(event, value) => setSelectedWorkout(value)}
          renderOption={(props, {
            id,
            label,
            equipment,
            workout,
            isValidWorkout,
            workoutStatus,
          }) => (
            <Tooltip
              key={id}
              title={!isValidWorkout ? format(texts.draftWorkout, { draftReason: workoutStatus }) : ''}
              arrow
              PopperProps={{
                disablePortal: true,
              }}
            >
              <StyledOption {...props} key={id} className="option-list">
                {label}
                <StyledOptionSubContent>
                  {!!equipment && (
                    <EquipmentChip label={format(texts.equipment, { equipment })} />
                  )}
                  {!!workout.estimatedDurationInMinutes && (
                    <TimeChip label={format(texts.estDuration, { minutes: workout.estimatedDurationInMinutes })} />
                  )}
                </StyledOptionSubContent>
              </StyledOption>
            </Tooltip>
          )}
        />
      </StyledAutocompleteWrapper>
      <RepeatContainer>
        {!!selectedWorkout && (
          <CheckBoxContainer>
            <StyledCheckBox
              onChange={handleCheckedRepeatWorkout}
              checked={shouldRepeatWorkout}
              size="small"
            />
            <StyledCheckBoxLabel>{texts.repeatWorkout}</StyledCheckBoxLabel>
          </CheckBoxContainer>
        )}
        {!!selectedWorkout && shouldRepeatWorkout && (
          <>
            <SettingSection>
              <StyledInputLabel>{texts.repeat}</StyledInputLabel>
              <DaysContainer>
                {moment.weekdaysShort().map((day) => (
                  <RepeatDay
                    key={day}
                    onClick={() => handleDaysSelect(day)}
                    isSelected={selectedDays.includes(day)}
                  >
                    {day}
                  </RepeatDay>
                ))}
              </DaysContainer>
            </SettingSection>
            <AssignmentRepeatInput
              helperText={texts.repeatHelperText}
              value={assignmentRepetitionWeeks}
              onChange={setAssignmentRepetitionWeeks}
              startDate={selectedDate}
              endDate={endDate}
            />
          </>
        )}
      </RepeatContainer>
      <LoadingOverlay isLoading={showLoading || !isReady} />
    </DialogRoundedModal>
  );
};

WorkoutAssignmentModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userDoc: PropTypes.instanceOf(User),
  selectedDate: PropTypes.string.isRequired,
};

WorkoutAssignmentModal.defaultProps = {
  userDoc: null,
};

export default compose(
  observer,
)(WorkoutAssignmentModal);
